import React from 'react';
import { motion } from 'framer-motion';

/**
 * Media Strip — Premium press-logo showcase.
 * Design intent: Rolex/Apple-style "As seen in" badge row.
 *
 * NOTE: These are typographic press marks (styled with serif Cinzel + gold accent
 * outlines) rather than raster logos. Reason: no licensed vector logos are in the
 * repo. When Guruji supplies real SVG logos, swap the `label` for `<img>`.
 */
const OUTLETS = [
  { name: 'Zee News' },
  { name: 'News18' },
  { name: 'Aaj Tak' },
  { name: 'India TV' },
  { name: 'ABP News' },
  { name: 'DD News' },
];

export default function MediaStrip() {
  const easeLux = [0.22, 1, 0.36, 1];

  return (
    <section
      className="relative py-12 lg:py-16"
      data-testid="media-strip"
    >
      <div className="container-custom max-w-6xl relative z-10">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: easeLux }}
          className="text-center mb-10 lg:mb-12"
        >
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
            <span className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37]/85 font-medium">
              As Featured In
            </span>
            <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
          </div>
        </motion.div>

        {/* Logo grid — 6 outlets, elegant typographic marks */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: easeLux, delay: 0.15 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-6 items-center justify-items-center"
        >
          {OUTLETS.map((o, i) => (
            <div
              key={o.name}
              className="group relative flex items-center justify-center h-14 w-full max-w-[180px]"
              data-testid={`media-logo-${o.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Subtle gold hairline on top */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-px bg-[#D4AF37]/0 group-hover:bg-[#D4AF37]/60 group-hover:w-14 transition-all duration-700" />
              <span
                className="font-heading text-[17px] sm:text-[19px] tracking-[0.2em] uppercase text-[#D0D0D0]/85 group-hover:text-[#F5F5F5] transition-colors duration-700"
                style={{ textShadow: '0 0 12px rgba(212,175,55,0.18)' }}
              >
                {o.name}
              </span>
              {i < OUTLETS.length - 1 && (
                <span className="hidden lg:block absolute right-[-12px] top-1/2 -translate-y-1/2 w-px h-6 bg-white/[0.06]" />
              )}
            </div>
          ))}
        </motion.div>

        {/* Subline */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: easeLux, delay: 0.35 }}
          className="text-center text-[11px] text-[#6E6E6E] tracking-[0.28em] uppercase mt-10 font-light"
        >
          200+ मीडिया साक्षात्कार · 33 वर्षों में
        </motion.p>
      </div>
    </section>
  );
}
